/**
 * /transport/international/:slug — Public profile + lead-inquiry CTA.
 *
 * Renders the courier's verified credentials (IATA / IPATA / CDC /
 * USDA APHIS / insurance), service corridors, airlines approved,
 * and bio. A "Request a quote" button opens a dialog that POSTs to
 * `/transport/international/{slug}/inquire` and routes the lead to
 * the courier via email (Reply-To = the buyer).
 *
 * NO escrow. NO on-platform payment. Armstrong is just the introducer.
 */
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import AppShell from "@/components/AppShell";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { AirplaneTiltIcon as AirplaneTilt, GlobeIcon as Globe, ShieldCheckIcon as ShieldCheck, CheckCircleIcon as CheckCircle, WarningIcon as Warning, ArrowLeftIcon as ArrowLeft } from "@phosphor-icons/react";

export default function InternationalTransporterProfile() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [courier, setCourier] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api
      .get(`/transport/international/${slug}`)
      .then((r) => { if (!cancelled) setCourier(r.data); })
      .catch(() => { if (!cancelled) setCourier(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [slug]);

  if (loading) {
    return (
      <AppShell>
        <div className="max-w-3xl mx-auto px-4 py-10">
          <Skeleton className="h-44 rounded-xl" />
        </div>
      </AppShell>
    );
  }
  if (!courier) {
    return (
      <AppShell>
        <div className="max-w-3xl mx-auto px-4 py-10 text-center">
          <h1 className="font-spectral text-3xl">Courier not found</h1>
          <Button
            variant="outline"
            className="rounded-full mt-4"
            onClick={() => navigate("/transport/international")}
          >
            ← Back to directory
          </Button>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <div
        className="max-w-3xl mx-auto px-4 py-8"
        data-testid={`intl-profile-${courier.slug}`}
      >
        <button
          onClick={() => navigate("/transport/international")}
          className="text-[12px] text-[#6B6B6B] hover:text-ink inline-flex items-center gap-1 mb-4"
        >
          <ArrowLeft size={12} /> All international couriers
        </button>

        <div className="bg-white border border-[#E5D08A] rounded-xl p-6 sm:p-8">
          <div className="flex items-start gap-5">
            {courier.photo_url ? (
              <img
                src={courier.photo_url}
                alt={courier.display_name}
                className="w-20 h-20 rounded-full object-cover border border-[#F0EAD6]"
              />
            ) : (
              <div className="w-20 h-20 rounded-full bg-[#FBF7EC] border border-[#F0EAD6] flex items-center justify-center">
                <Globe size={40} weight="duotone" className="text-[#8A6A00]" />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <h1 className="font-spectral text-3xl tracking-tight">
                {courier.display_name}
              </h1>
              <div className="text-[13px] text-[#6B6B6B] mt-1">
                Based in <strong>{courier.home_country}</strong>
                {courier.years_in_business > 0 && (
                  <> · {courier.years_in_business} yrs in business</>
                )}
                {courier.trips_completed_estimate > 0 && (
                  <> · ~{courier.trips_completed_estimate}+ trips</>
                )}
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                {courier.verified_badge_at && (
                  <Badge tone="gold">
                    <ShieldCheck size={12} weight="duotone" /> Armstrong Verified
                  </Badge>
                )}
                {courier.iata_lar_certified && (
                  <Badge tone="green">
                    <CheckCircle size={12} weight="fill" /> IATA LAR Certified
                  </Badge>
                )}
                {courier.ipata_member_number_verified && (
                  <Badge tone="green">
                    <CheckCircle size={12} weight="fill" /> IPATA Member
                  </Badge>
                )}
                {courier.cdc_permit_verified && (
                  <Badge tone="green">
                    <CheckCircle size={12} weight="fill" /> CDC Import Permit
                  </Badge>
                )}
                {courier.usda_aphis_verified && (
                  <Badge tone="green">
                    <CheckCircle size={12} weight="fill" /> USDA APHIS
                  </Badge>
                )}
                {courier.insurance_verified && (
                  <Badge tone="green">
                    <CheckCircle size={12} weight="fill" /> Insured
                    {courier.insurance_liability_usd > 0
                      ? ` ($${(courier.insurance_liability_usd / 1000).toFixed(0)}k+)`
                      : ""}
                  </Badge>
                )}
              </div>
            </div>
            <Button
              onClick={() => setOpen(true)}
              className="rounded-full bg-crimson hover:bg-ink text-white text-[12px] h-10 px-5 flex-shrink-0"
              data-testid="intl-cta-quote"
            >
              Request a quote
            </Button>
          </div>

          {courier.bio && (
            <div className="mt-6 text-[14px] leading-relaxed text-ink">
              {courier.bio}
            </div>
          )}

          <Section title="Corridors served">
            <div className="flex flex-wrap gap-1.5">
              {(courier.corridors || []).map((c) => (
                <span
                  key={c}
                  className="text-[12px] px-2.5 py-1 rounded-full bg-[#FBF7EC] border border-[#F0EAD6]"
                >
                  {c}
                </span>
              ))}
            </div>
          </Section>

          {(courier.airlines_approved || []).length > 0 && (
            <Section title="Approved airlines">
              <div className="flex flex-wrap gap-1.5">
                {courier.airlines_approved.map((a) => (
                  <span
                    key={a}
                    className="text-[12px] px-2.5 py-1 rounded-full bg-white border border-[#E5E5E5]"
                  >
                    <AirplaneTilt
                      size={12}
                      weight="duotone"
                      className="inline mr-1 -mt-0.5"
                    />
                    {a}
                  </span>
                ))}
              </div>
            </Section>
          )}
        </div>

        {/* ============= Venue Disclaimer ============= */}
        <div className="mt-6 bg-[#FBF7EC] border border-[#E5D08A] rounded-xl p-4 text-[11px] text-[#5C5C5C] leading-relaxed flex gap-3">
          <Warning
            size={20}
            weight="duotone"
            className="text-[#8A6A00] flex-shrink-0 mt-0.5"
          />
          <div>
            <strong>Armstrong is a venue.</strong> We introduce you to
            this independent courier — we are NOT the shipper, importer,
            customs broker, or carrier. Verify all credentials, contracts,
            and pricing directly with the courier. All money flows
            off-platform between you and the courier.
          </div>
        </div>
      </div>

      <QuoteDialog
        open={open}
        onOpenChange={setOpen}
        slug={slug}
        courier={courier}
      />
    </AppShell>
  );
}

function Section({ title, children }) {
  return (
    <div className="mt-6 border-t border-[#F0EAD6] pt-5">
      <div className="text-[10px] tracking-[2px] text-[#8A6A00] font-bold uppercase mb-2">
        {title}
      </div>
      {children}
    </div>
  );
}

function Badge({ tone = "gold", children }) {
  const cls =
    tone === "green"
      ? "bg-[#E9F8EE] border-[#C7E9CE] text-[#1A6E36]"
      : "bg-[#FBF7EC] border-[#E5D08A] text-[#8A6A00]";
  return (
    <span
      className={`inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full border ${cls}`}
    >
      {children}
    </span>
  );
}

function QuoteDialog({ open, onOpenChange, slug, courier }) {
  const [form, setForm] = useState({
    buyer_name: "",
    buyer_email: "",
    buyer_phone: "",
    origin_country: "",
    destination_country: "USA",
    destination_city: "",
    requested_pickup_date: "",
    dog_count: 1,
    breed: "",
    notes: "",
  });
  const [ack, setAck] = useState(false);
  const [busy, setBusy] = useState(false);

  const set = (k) => (e) =>
    setForm((s) => ({
      ...s,
      [k]: e?.target ? e.target.value : e,
    }));

  const submit = async () => {
    if (!ack) {
      toast.error("Please acknowledge the venue disclaimer to continue.");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post(
        `/transport/international/${slug}/inquire`,
        { ...form, venue_acknowledged: true }
      );
      if (r.data?.requires_buyer_payment && r.data?.stripe_checkout_url) {
        toast.info(
          `Verified intro fee ($${r.data.amount_usd}) &mdash; redirecting to checkout…`
        );
        window.location.assign(r.data.stripe_checkout_url);
        return;
      }
      toast.success(r.data?.message || "Sent.");
      onOpenChange(false);
    } catch (e) {
      toast.error(
        e?.response?.data?.detail ||
          "We couldn't send your inquiry. Please try again."
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-lg"
        data-testid="intl-quote-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-spectral text-2xl">
            Request a quote from {courier?.display_name}
          </DialogTitle>
          <DialogDescription className="text-[12px]">
            We&rsquo;ll forward your details directly to the courier. Their reply
            goes straight to your email &mdash; Armstrong is not part of the
            negotiation.
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
          <Input
            placeholder="Your full name"
            value={form.buyer_name}
            onChange={set("buyer_name")}
            data-testid="intl-quote-name"
          />
          <Input
            placeholder="Your email"
            type="email"
            value={form.buyer_email}
            onChange={set("buyer_email")}
            data-testid="intl-quote-email"
          />
          <Input
            placeholder="Phone (optional)"
            value={form.buyer_phone}
            onChange={set("buyer_phone")}
          />
          <Input
            placeholder="Pickup date (flexible OK)"
            value={form.requested_pickup_date}
            onChange={set("requested_pickup_date")}
          />
          <Input
            placeholder="Origin country"
            value={form.origin_country}
            onChange={set("origin_country")}
            data-testid="intl-quote-origin"
          />
          <Input
            placeholder="Destination country"
            value={form.destination_country}
            onChange={set("destination_country")}
            data-testid="intl-quote-destination"
          />
          <Input
            placeholder="Destination city / airport"
            value={form.destination_city}
            onChange={set("destination_city")}
          />
          <Input
            placeholder="Breed (optional)"
            value={form.breed}
            onChange={set("breed")}
          />
        </div>
        <Textarea
          placeholder="Notes for the courier (puppy age, kennel size, vet docs in hand, etc.)"
          value={form.notes}
          onChange={set("notes")}
          rows={3}
          className="mt-2"
          data-testid="intl-quote-notes"
        />
        <label className="flex gap-2 items-start mt-2 text-[11px] leading-relaxed text-[#5C5C5C]">
          <Checkbox
            checked={ack}
            onCheckedChange={(v) => setAck(!!v)}
            data-testid="intl-quote-ack"
          />
          <span>
            I understand Armstrong K9 Registry is a venue and is NOT the
            shipper, importer, broker, or carrier. I will verify all
            credentials and contract directly with the courier. All money
            flows off-platform.
          </span>
        </label>
        <DialogFooter className="mt-4">
          <Button
            variant="outline"
            className="rounded-full"
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            className="rounded-full bg-crimson hover:bg-ink text-white"
            disabled={busy || !ack}
            onClick={submit}
            data-testid="intl-quote-submit"
          >
            {busy ? "Sending…" : "Send inquiry"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
